import React from 'react';
import styled from 'styled-components';
import {
  FieldType,
  ISimplePredicateFieldsFragment,
  RelationalOperatorKey,
} from '../../../../../utils/apollo/graphqlSchema';
import { colors } from '../../../../../config/styles';
import BasicFilterPredicateField from './BasicFilterPredicateField';
import {
  DisplayDateFormat,
  globalFormat,
  timeFormatDictionary,
} from '../../../../../utils/formatting';
import { assertCondition, PickEnum } from '../../../../../utils/types';
import { theme } from '../../../../../styles/theme';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  predicate: ISimplePredicateFieldsFragment;
  keyPrefix: string;
}

const OperandName = styled.div<{ $isField: boolean }>`
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: ${({ $isField }) =>
    $isField ? colors.purple : theme.colors.mainBlue};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RelOpName = styled.div`
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: ${theme.colors.mainBlue};
`;

export const fieldDateFormatDictionary: Record<
  DisplayDateFormat,
  Record<
    PickEnum<
      FieldType,
      | FieldType.Timestamp
      | FieldType.Datetime
      | FieldType.Month
      | FieldType.Date
      | FieldType.Week
    >,
    string
  >
> = {
  'DD.MM.YYYY': {
    [FieldType.Date]: 'D.M.YYYY',
    [FieldType.Timestamp]: 'D.M.YYYY',
    [FieldType.Datetime]: 'D.M.YYYY',
    [FieldType.Week]: 'D.M.YYYY',
    [FieldType.Month]: 'M.YYYY',
  },
  'MM/DD/YYYY': {
    [FieldType.Date]: 'M/D/YYYY',
    [FieldType.Timestamp]: 'M/D/YYYY',
    [FieldType.Datetime]: 'M/D/YYYY',
    [FieldType.Week]: 'M/D/YYYY',
    [FieldType.Month]: 'M/YYYY',
  },
  'YYYY-MM-DD': {
    [FieldType.Date]: 'YYYY-M-D',
    [FieldType.Timestamp]: 'YYYY-M-D',
    [FieldType.Datetime]: 'YYYY-M-D',
    [FieldType.Week]: 'YYYY-M-D',
    [FieldType.Month]: 'YYYY-M',
  },
  'DD/MM/YYYY': {
    [FieldType.Date]: 'D/M/YYYY',
    [FieldType.Timestamp]: 'D/M/YYYY',
    [FieldType.Datetime]: 'D/M/YYYY',
    [FieldType.Week]: 'D/M/YYYY',
    [FieldType.Month]: 'M/YYYY',
  },
};

const SimpleFilterPredicate = ({ predicate, keyPrefix }: IProps) => {
  const { field, relOp, operands } = predicate;
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const isField = operands.__typename === 'FieldOperands';

  if (relOp.key === RelationalOperatorKey.Between) {
    if (isField) {
      return (
        <>
          <OperandName $isField={isField}>
            {operands.fields[0].name}
          </OperandName>
          <RelOpName>≤</RelOpName>
          <BasicFilterPredicateField field={field} />
          <RelOpName>≤</RelOpName>
          <OperandName $isField={isField}>
            {operands.fields[1].name}
          </OperandName>
        </>
      );
    }
  }

  // if the operand field type is a date type, format the operand name by date
  // otherwise return the operand name
  const getFormattedOperandName = (
    operandName: string,
    fieldType: FieldType,
    isOperandField: boolean,
  ) => {
    const dateTypes = [
      FieldType.Date,
      FieldType.Timestamp,
      FieldType.Datetime,
      FieldType.Week,
      FieldType.Month,
    ];

    if (
      !isOperandField &&
      dateTypes.includes(fieldType) &&
      relOp.key !== RelationalOperatorKey.Exists
    ) {
      assertCondition(
        fieldType === FieldType.Date ||
          fieldType === FieldType.Timestamp ||
          fieldType === FieldType.Datetime ||
          fieldType === FieldType.Week ||
          fieldType === FieldType.Month,
      );
      let format = '';
      let sourceFormat;
      switch (fieldType) {
        case FieldType.Date:
        case FieldType.Week:
          format = `${fieldDateFormatDictionary[dateFormat][fieldType]}`;
          break;
        case FieldType.Month:
          format = `${fieldDateFormatDictionary[dateFormat][fieldType]}`;
          sourceFormat = format;
          break;
        case FieldType.Timestamp:
        case FieldType.Datetime:
          format = `${fieldDateFormatDictionary[dateFormat][fieldType]} ${timeFormatDictionary[timeFormat]}`;
          break;
        default:
          throw new UnreachableCaseError(fieldType);
      }

      // fix(KBT-507) special case to indicate source format,moment doesn't recognize monthType: 10/2020,
      // so we need to indicate the source format
      return globalFormat.formatDate(operandName, format, sourceFormat);
    } else {
      return operandName;
    }
  };

  return (
    <>
      <BasicFilterPredicateField field={field} />
      <RelOpName>{relOp.name}</RelOpName>
      {operands.__typename === 'FieldOperands'
        ? operands.fields.map((operand, operandIndex) => {
            const key = keyPrefix + `-operand-${operandIndex}`;
            const operandName = getFormattedOperandName(
              operand.name,
              field.type,
              isField,
            );
            return (
              <OperandName key={key} $isField={isField} title={operandName}>
                {operandName}
                {operandIndex < operands.fields.length - 1 && ','}
              </OperandName>
            );
          })
        : operands.values.map((operand, operandIndex) => {
            const key = keyPrefix + `-operand-${operandIndex}`;
            const operandName = getFormattedOperandName(
              operand.name,
              field.type,
              isField,
            );
            return (
              <OperandName key={key} $isField={isField} title={operandName}>
                {operandName}
                {operandIndex < operands.values.length - 1 && ','}
              </OperandName>
            );
          })}
    </>
  );
};

export default SimpleFilterPredicate;
